import { useQuery } from '@tanstack/react-query';
import {
    GridCallbackDetails,
    GridColDef,
    GridPaginationModel,
    GridRowParams,
    GridSortModel,
} from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    pourAvailabilityName,
} from '@barboxx/admin-components';
import {
    ACTIVE_WIDTH,
    DEFAULT_WIDTH,
    NAME_MIN_WIDTH,
    PRICE_WIDTH,
    renderPriceEditCell,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessPourAvailability, PourAvailability } from '@barboxx/models';

export type Column = GridColDef<BusinessPourAvailability.DetailsResponse>;
export type Columns = Column[];

export const COLUMNS: Columns = [
    {
        field: 'basePrice',
        headerName: 'Base Price',
        minWidth: PRICE_WIDTH,
        type: 'number',
        editable: true,
        renderEditCell: renderPriceEditCell,
    },
    {
        field: 'upCharge',
        headerName: 'Up Charge',
        minWidth: PRICE_WIDTH,
        type: 'number',
        editable: true,
        renderEditCell: renderPriceEditCell,
    },
    {
        field: 'notAllowed',
        headerName: 'Not Allowed',
        width: DEFAULT_WIDTH,
        editable: true,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'isActive',
        headerName: 'Active',
        width: ACTIVE_WIDTH,
        editable: true,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'isDefault',
        headerName: 'Default',
        width: DEFAULT_WIDTH,
        editable: true,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
];

interface Props {
    data: BusinessPourAvailability[];
    size: number;
    page: number;
    total: number;
    onRowClick: (id: string) => void;
    onChangeSort: (sort: GridSortModel) => void;
    handleChangePageModel: (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => void;
}

export function PourAvailabilityTable(props: Props) {
    const {
        data = [],
        total,
        size,
        page,
        onRowClick,
        handleChangePageModel,
        onChangeSort,
    } = props;

    const { businessList } = useBusinessSelector();

    const pourAvailabilityQuery = useQuery({
        queryKey: ['pourAvailabilities'],
        queryFn: () =>
            Api.Menu.PourAvailability.getPourAvailabilities({
                page: 0,
                size: 1000,
            }),
    });

    const handleRowClick = (param: GridRowParams) => {
        onRowClick(param.id as string);
    };

    const columns: Columns = [
        {
            field: 'businessId',
            headerName: 'Business ID',
            minWidth: NAME_MIN_WIDTH,
            type: 'singleSelect',
            flex: 1,
            valueOptions: params =>
                businessList.map(business => ({
                    value: business.id,
                    label: business.place.name,
                })) ?? [],
        },
        {
            field: 'pourAvailabilityId',
            headerName: 'Pour Availability',
            editable: true,
            minWidth: NAME_MIN_WIDTH,
            type: 'singleSelect',
            flex: 1,
            valueOptions: params =>
                pourAvailabilityQuery.data?.data?.results.map(
                    (pourAvailability: PourAvailability) => ({
                        value: pourAvailability.id,
                        label: pourAvailabilityName(pourAvailability),
                    })
                ) ?? [],
        },
        ...COLUMNS,
        // {
        //     field: 'actions',
        //     type: 'actions',
        //     headerName: 'Actions',
        //     width: ACTIONS_WIDTH,
        //     cellClassName: 'actions',
        //     display: 'flex',
        //     align: 'right',
        //     getActions: (
        //         params: GridRowParams<BusinessPourAvailability>
        //     ) => [
        //         <EditSaveAction key="editSave" {...params} />,
        //         <CancelRelateAction
        //             key="cancelRelate"
        //             {...params}
        //             onDelete={onDelete}
        //         />,
        //     ],
        // },
    ];

    return (
        <DataGridCard title="Pour Availability">
            <DataGrid
                rows={data}
                columns={columns}
                paginationModel={{
                    page,
                    pageSize: size,
                }}
                onPaginationModelChange={handleChangePageModel}
                rowCount={total}
                disableColumnMenu
                onRowClick={handleRowClick}
                onSortModelChange={onChangeSort}
            />
        </DataGridCard>
    );
}
